/**
 * 2026-04-25 Phase 32-B.1 — 제련·각인 의식 API 클라이언트.
 *
 * 백엔드: `rpg-app/src/main/java/io/tagdog/rpg/handler/ForgeHandler.java`
 * 경로 prefix: `/api/v1/public/game/runs/{runId}/forge/...`
 *
 * 제련 (4스텝): start → continue* → stop (또는 4스텝 자동 종료)
 * 각인 의식: start → selectOmen → addAid* → confirm
 */
import { apiGet, apiPost } from "@/api/client";
import type { InventoryItemView } from "@/api/types";

// ============================================================================
// 4-state 결과 enum
// ============================================================================

export type ForgeOutcome = "CRIT_SUCCESS" | "SUCCESS" | "FAIL" | "CRIT_FAIL";

// ============================================================================
// 제련 (Refine) 타입
// ============================================================================

export interface StepCost {
    refineStoneCount: number;
    gold: number;
    refineStoneId: string;
}

export interface RefineStepResult {
    sessionId: string;
    stepIndex: number;           // 1~4
    stepSuccess: boolean;        // 이 스텝의 성공 여부
    sequence: string;            // 누적 "SSFS"
    canStop: boolean;            // 2스텝 이후만 true
    canContinue: boolean;        // 4스텝 전이면 true
    previewIfStop: ForgeOutcome;
    previewIfContinue?: ForgeOutcome[];
    nextStepCost?: StepCost | null;
    nextStepOddsPct?: number | null;
}

export interface RefineTotalCost {
    refineStones: number;
    gold: number;
}

export interface RefineFinalResult {
    item: InventoryItemView | null;   // 대실패 파괴 시 null
    outcome: ForgeOutcome;
    fullSequence: string;
    enhancementBefore: number;
    enhancementAfter: number | null;   // 파괴 시 null
    destroyed: boolean;
    artisanAshGained: number;
    stepsUsed: number;
    totalCost: RefineTotalCost;
    overridenOutcome?: string | null;  // "TRANSCENDENT" 초월 대성공
}

// ============================================================================
// 각인 의식 (Engrave Ceremony) 타입
// ============================================================================

export type OmenType = "HOLD" | "BET" | "PRESS" | "NONE";

export type MaterialGrade = "" | "standard" | "refined" | "crystal";

export type AidId = "aid.stability_token" | "aid.polar_ore" | "aid.seal_stone";

export interface Odds {
    great: number;
    success: number;
    fail: number;
    critFail: number;
}

export interface CeremonyState {
    sessionId: string;
    engraveId: string;
    materialGrade: MaterialGrade;
    cracks: number;
    omenStrength: number;            // 5~15
    selectedOmen: OmenType;          // 초기 "NONE"
    aids: AidId[];                    // 최대 2개
    currentOdds: Odds;               // 전체 반영 (omen + aid 포함)
    baseOdds: Odds;                  // omen/aid 적용 전 (재료 + tuneCurve + crack)
    cost: number;                    // 예정 gold 비용
}

export interface EngraveCeremonyResult {
    item: InventoryItemView;
    outcome: ForgeOutcome;
    engraveTier?: "SUCCESS" | "GREAT" | null;  // FAIL/CRIT_FAIL 시 null
    materialGrade: MaterialGrade;
    cracksAfter?: number | null;      // CRIT_FAIL 후 균열 카운터
    failureMessage?: string | null;
    omenUsed: OmenType;
    aidsUsed?: AidId[];
}

// ============================================================================
// 제련 API
// ============================================================================

export function refineStart(runId: string, instanceId: string): Promise<RefineStepResult> {
    return apiPost<RefineStepResult>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/forge/refine/start`,
        { instanceId },
    );
}

export function refineContinue(runId: string, sessionId: string): Promise<RefineStepResult> {
    return apiPost<RefineStepResult>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/forge/refine/${encodeURIComponent(sessionId)}/continue`,
        {},
    );
}

export function refineStop(runId: string, sessionId: string): Promise<RefineFinalResult> {
    return apiPost<RefineFinalResult>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/forge/refine/${encodeURIComponent(sessionId)}/stop`,
        {},
    );
}

// ============================================================================
// 각인 의식 API
// ============================================================================

export function engraveCeremonyStart(
    runId: string,
    instanceId: string,
    engraveId: string,
    materialInstanceId?: string | null,
): Promise<CeremonyState> {
    return apiPost<CeremonyState>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/forge/engrave/start`,
        { instanceId, engraveId, materialInstanceId: materialInstanceId ?? null },
    );
}

export function engraveSelectOmen(
    runId: string,
    sessionId: string,
    omenType: OmenType,
): Promise<CeremonyState> {
    return apiPost<CeremonyState>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/forge/engrave/${encodeURIComponent(sessionId)}/omen`,
        { omenType },
    );
}

export function engraveAddAid(
    runId: string,
    sessionId: string,
    aidId: AidId,
): Promise<CeremonyState> {
    return apiPost<CeremonyState>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/forge/engrave/${encodeURIComponent(sessionId)}/aid`,
        { aidId, remove: false },
    );
}

export function engraveRemoveAid(
    runId: string,
    sessionId: string,
    aidId: AidId,
): Promise<CeremonyState> {
    return apiPost<CeremonyState>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/forge/engrave/${encodeURIComponent(sessionId)}/aid`,
        { aidId, remove: true },
    );
}

export function engraveConfirm(
    runId: string,
    sessionId: string,
): Promise<EngraveCeremonyResult> {
    return apiPost<EngraveCeremonyResult>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/forge/engrave/${encodeURIComponent(sessionId)}/confirm`,
        {},
    );
}

/**
 * 2026-04-25 Phase 32 P1 — 수리석으로 장비 균열 1개 복구.
 * 인벤토리에 `material.repair_stone` 1개 필요. engraveCracks 가 0 이면 400.
 */
export function repairCrack(
    runId: string,
    instanceId: string,
): Promise<InventoryItemView> {
    return apiPost<InventoryItemView>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/forge/engrave/${encodeURIComponent(instanceId)}/repair-crack`,
        {},
    );
}

// ============================================================================
// Phase 32-C.7 — 카탈로그 · 통계 · 분해
// ============================================================================

export interface EngraveCatalogEntry {
    id: string;
    label: string;
    tier: "common" | "uncommon" | "rare" | "epic" | "legendary";
    tuneCurveCritFail: number;
    tuneCurveGreat: number;
    successSummary: string | null;
    greatSummary: string | null;
}

/** 각인 11종 × tier 메타 + tuneCurve. 툴팁·가이드 UI 에서 사용. */
export function fetchEngraveCatalog(): Promise<EngraveCatalogEntry[]> {
    return apiGet<EngraveCatalogEntry[]>("/api/v1/public/game/engraves");
}

export interface ForgeStats {
    highestEnhancement: number;
    highestEnhItemName: string | null;
    itemsAtPlus7OrAbove: number;
    itemsAtPlus10: number;
    greatTierCount: number;
    successTierCount: number;
    totalCracks: number;
    totalPityTokens: number;
    totalArtisanAsh: number;
    totalRepairStones: number;
    refineStonesBasic: number;
    refineStonesRare: number;
    stabilityTokens: number;
    polarOres: number;
    sealStones: number;
}

/** 현재 런의 제련·각인 통계 — 보유 재료 + 최고 단계 + 균열 등 스냅샷. */
export function fetchForgeStats(runId: string): Promise<ForgeStats> {
    return apiGet<ForgeStats>(`/api/v1/public/game/runs/${encodeURIComponent(runId)}/forge/stats`);
}

export interface SalvageResult {
    itemName: string;
    enhancementBefore: number;
    hadEngrave: boolean;
    artisanAshGained: number;
    refineStoneGained: number;
    refineStoneId: string;
    pityTokenBonus: number;
}

/**
 * 장비 분해 — 스스로 파괴해서 장인의 재 + 제련석 + (각인 있으면) 연민의 증표 환원.
 *
 *   ash  = max(1, enhancement / 2) + (hasEngrave ? 1 : 0)
 *   stone = enhancement * 2 (구간 3 이상이면 별철)
 *   pity  = 1 (engrave 있었을 경우)
 */
export function salvageItem(runId: string, instanceId: string): Promise<SalvageResult> {
    return apiPost<SalvageResult>(
        `/api/v1/public/game/runs/${encodeURIComponent(runId)}/inventory/${encodeURIComponent(instanceId)}/salvage`,
        {},
    );
}

// ============================================================================
// 보조 헬퍼
// ============================================================================

/** 4-state outcome 을 한글 표기로. */
export function outcomeLabel(o: ForgeOutcome): string {
    switch (o) {
        case "CRIT_SUCCESS": return "대성공";
        case "SUCCESS":      return "성공";
        case "FAIL":         return "실패";
        case "CRIT_FAIL":    return "대실패";
    }
}

/** 4-state outcome 심볼. */
export function outcomeSymbol(o: ForgeOutcome): string {
    switch (o) {
        case "CRIT_SUCCESS": return "✦";
        case "SUCCESS":      return "✧";
        case "FAIL":         return "✕";
        case "CRIT_FAIL":    return "☠";
    }
}

/** 재료 등급 한글. */
export function materialGradeLabel(g: MaterialGrade): string {
    switch (g) {
        case "":          return "재료 없음";
        case "standard":  return "일반";
        case "refined":   return "정제";
        case "crystal":   return "결정";
    }
}

/** 징조 프리셋 한글. */
export function omenLabel(o: OmenType): string {
    switch (o) {
        case "HOLD":  return "붙든다";
        case "BET":   return "건다";
        case "PRESS": return "누른다";
        case "NONE":  return "유지";
    }
}

/** 징조 심볼. */
export function omenSymbol(o: OmenType): string {
    switch (o) {
        case "HOLD":  return "🛡";
        case "BET":   return "🔥";
        case "PRESS": return "⚖";
        case "NONE":  return "—";
    }
}

/** 보조 아이템 한글. */
export function aidLabel(a: AidId): string {
    switch (a) {
        case "aid.stability_token": return "안정의 정표";
        case "aid.polar_ore":       return "양극의 광석";
        case "aid.seal_stone":      return "봉인석";
    }
}
